import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Ribbon, LogOut, Store, Mail, Ban, Trash2, AlertTriangle, X, Settings, KeyRound, ImagePlus, Phone, Lock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PageHead } from "@/components/common";
import { api } from "@/lib/api";

function fileToDataUrl(file, max = 640) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = reject;
    r.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
        const c = document.createElement("canvas"); c.width = w; c.height = h;
        c.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(c.toDataURL("image/jpeg", 0.85));
      };
      img.onerror = () => resolve(r.result);
      img.src = r.result;
    };
    r.readAsDataURL(file);
  });
}

function BlockedList() {
  const [blocked, setBlocked] = useState(null);
  const load = () => api.get("/blocks").then((d) => setBlocked(d.blocked)).catch(() => setBlocked([]));
  useEffect(() => { load(); }, []);
  if (blocked === null || blocked.length === 0) return null;

  const unblock = async (id, name) => {
    try { await api.del(`/blocks/${id}`); toast.success(`Unblocked ${name}`); load(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div className="ss-card" style={{ padding: 22, marginTop: 18 }} data-testid="blocked-list">
      <div style={{ fontWeight: 800, fontSize: 18, display: "flex", alignItems: "center", gap: 8 }}><Ban size={18} style={{ color: "var(--red)" }} /> Blocked users</div>
      <p className="ss-muted" style={{ fontSize: 13, margin: "6px 0 16px" }}>They can't message you and you don't see their messages. Unblock anytime.</p>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {blocked.map((b) => (
          <div key={b.user_id} className="ss-user-row" style={{ padding: "8px 4px" }} data-testid={`blocked-${b.user_id}`}>
            <img src={b.avatar} alt="" style={{ width: 40, height: 40, borderRadius: 999, objectFit: "cover", border: "1px solid var(--line)" }} />
            <div style={{ flex: 1, fontWeight: 700 }}>{b.name}</div>
            <button className="ss-btn ss-btn-ghost ss-btn-sm" onClick={() => unblock(b.user_id, b.name)} data-testid={`unblock-${b.user_id}`}>Unblock</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function DeleteAccountModal({ onClose }) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const ready = password.length >= 1 && confirm.trim().toUpperCase() === "DELETE";

  const doDelete = async () => {
    if (!ready) return;
    setBusy(true);
    try {
      await api.post("/auth/delete-account", { password });
      toast.success("Your account has been permanently deleted.");
      await logout();
      navigate("/landing");
    } catch (e) {
      toast.error(e.message || "Could not delete account");
      setBusy(false);
    }
  };

  return (
    <div className="ss-modal-back" onClick={onClose}>
      <div className="ss-modal" onClick={(e) => e.stopPropagation()} data-testid="delete-account-modal">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <h2 style={{ fontSize: 20, color: "var(--red)", display: "flex", alignItems: "center", gap: 8 }}><AlertTriangle size={20} /> Delete account</h2>
          <button type="button" className="disc-search" style={{ width: 34, height: 34 }} onClick={onClose} data-testid="delete-account-close"><X size={18} /></button>
        </div>
        <p className="ss-muted" style={{ fontSize: 14, marginBottom: 16, lineHeight: 1.6 }}>
          This <b>permanently deletes</b> your account and personal data. Your saved items, offers, messages and invite
          codes will be removed. Past orders and payments are kept but anonymized for legal and accounting reasons.
          <b> This cannot be undone.</b>
        </p>
        <label className="ss-label">Confirm your password</label>
        <div className="ss-field" style={{ marginBottom: 12 }}><input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="delete-account-password" /></div>
        <label className="ss-label">Type <b>DELETE</b> to confirm</label>
        <div className="ss-field" style={{ marginBottom: 16 }}><input placeholder="DELETE" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="delete-account-confirm" /></div>
        <button className="ss-btn ss-btn-block" style={{ background: "var(--red)", color: "#fff", opacity: ready && !busy ? 1 : 0.55 }} disabled={!ready || busy} onClick={doDelete} data-testid="delete-account-submit">
          <Trash2 size={16} /> {busy ? "Deleting…" : "Permanently delete my account"}
        </button>
      </div>
    </div>
  );
}

function AccountSettings() {
  const { user, refresh } = useAuth();
  const avatarInput = useRef(null);
  const [form, setForm] = useState({ name: user.name || "", username: user.username || "", phone: user.phone || "", store_name: user.store_name || "", bio: user.bio || "", avatar: user.avatar || "" });
  const [saving, setSaving] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const onAvatar = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("Please choose an image");
    try { const img = await fileToDataUrl(f); setForm((s) => ({ ...s, avatar: img })); } catch { toast.error("Couldn't read image"); }
  };

  const saveProfile = async () => {
    if (!form.name.trim()) return toast.error("Name can't be empty");
    setSaving(true);
    try { await api.patch("/profile", { name: form.name.trim(), username: form.username.trim(), phone: form.phone.trim(), store_name: form.store_name.trim(), bio: form.bio.trim(), avatar: form.avatar }); await refresh(); toast.success("Profile updated"); }
    catch (e) { toast.error(e.message || "Could not save"); } finally { setSaving(false); }
  };

  const savePassword = async () => {
    if (pw.new_password.length < 6) return toast.error("New password must be at least 6 characters");
    if (pw.new_password !== pw.confirm) return toast.error("New passwords don't match");
    setPwBusy(true);
    try { await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password }); toast.success("Password updated"); setPw({ current_password: "", new_password: "", confirm: "" }); }
    catch (e) { toast.error(e.message || "Could not change password"); } finally { setPwBusy(false); }
  };

  return (
    <div className="ss-card" style={{ padding: 24, marginTop: 18 }} data-testid="account-settings">
      <h3 style={{ fontSize: 18, fontWeight: 800, display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}><Settings size={18} /> Account settings</h3>
      <p className="ss-muted" style={{ fontSize: 13, marginBottom: 18 }}>Update how you appear on SouqStream.</p>

      <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 16 }}>
        <input ref={avatarInput} type="file" accept="image/*" style={{ display: "none" }} onChange={onAvatar} data-testid="settings-avatar-input" />
        <img src={form.avatar} alt="" style={{ width: 72, height: 72, borderRadius: 999, objectFit: "cover", border: "2px solid var(--line)" }} />
        <button className="ss-btn ss-btn-ghost ss-btn-sm" onClick={() => avatarInput.current?.click()} data-testid="settings-change-avatar"><ImagePlus size={14} /> Change photo</button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div><label className="ss-label">Display name</label><div className="ss-field" style={{ height: 44 }}><input value={form.name} onChange={set("name")} data-testid="settings-name" /></div></div>
        <div><label className="ss-label">Store name</label><div className="ss-field" style={{ height: 44 }}><input placeholder="Optional" value={form.store_name} onChange={set("store_name")} data-testid="settings-store-name" /></div></div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
        <div>
          <label className="ss-label">Username</label>
          <div className="ss-field" style={{ height: 44 }}><span className="ss-muted">@</span><input placeholder="username" value={form.username} onChange={(e) => setForm((s) => ({ ...s, username: e.target.value.replace(/^@/, "") }))} data-testid="settings-username" /></div>
          <div className="ss-muted" style={{ fontSize: 12, marginTop: 4 }}>3-20 chars: letters, numbers, dots or underscores.</div>
        </div>
        <div>
          <label className="ss-label">Phone number</label>
          <div className="ss-field" style={{ height: 44 }}><Phone size={15} className="ss-muted" /><input type="tel" placeholder="+1 555 123 4567" value={form.phone} onChange={set("phone")} data-testid="settings-phone" /></div>
          <div className="ss-muted" style={{ fontSize: 12, marginTop: 4, display: "flex", alignItems: "center", gap: 4 }}><Lock size={11} /> Private — only visible to SouqStream admins.</div>
        </div>
      </div>
      <div style={{ marginTop: 12 }}>
        <label className="ss-label">Bio</label>
        <div className="ss-field" style={{ height: "auto", padding: 0 }}>
          <textarea rows={2} placeholder="Tell shoppers about you" value={form.bio} onChange={set("bio")} style={{ width: "100%", border: 0, background: "transparent", padding: 12, resize: "vertical", font: "inherit", color: "inherit", outline: "none" }} data-testid="settings-bio" />
        </div>
      </div>
      <button className="ss-btn ss-btn-primary" style={{ marginTop: 14 }} onClick={saveProfile} disabled={saving} data-testid="settings-save-profile">{saving ? "Saving…" : "Save changes"}</button>

      <div style={{ borderTop: "1px solid var(--line)", margin: "22px 0 16px" }} />
      <h4 style={{ fontSize: 15, fontWeight: 800, display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}><KeyRound size={16} /> Change password</h4>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
        <div><label className="ss-label">Current</label><div className="ss-field" style={{ height: 44 }}><input type="password" value={pw.current_password} onChange={(e) => setPw((s) => ({ ...s, current_password: e.target.value }))} data-testid="settings-current-password" /></div></div>
        <div><label className="ss-label">New</label><div className="ss-field" style={{ height: 44 }}><input type="password" value={pw.new_password} onChange={(e) => setPw((s) => ({ ...s, new_password: e.target.value }))} data-testid="settings-new-password" /></div></div>
        <div><label className="ss-label">Confirm new</label><div className="ss-field" style={{ height: 44 }}><input type="password" value={pw.confirm} onChange={(e) => setPw((s) => ({ ...s, confirm: e.target.value }))} data-testid="settings-confirm-password" /></div></div>
      </div>
      <button className="ss-btn ss-btn-ghost" style={{ marginTop: 14 }} onClick={savePassword} disabled={pwBusy || !pw.current_password || !pw.new_password} data-testid="settings-save-password">{pwBusy ? "Updating…" : "Update password"}</button>
    </div>
  );
}

export default function Profile() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [showDelete, setShowDelete] = useState(false);
  if (!user) return null;

  return (
    <div className="fade-up" style={{ maxWidth: 680, margin: "0 auto" }}>
      <PageHead title="Your Profile" />
      <div className="ss-card" style={{ padding: 26 }}>
        <div style={{ display: "flex", gap: 18, alignItems: "center" }}>
          <img src={user.avatar} alt={user.name} style={{ width: 88, height: 88, borderRadius: 999, objectFit: "cover", border: "3px solid var(--gold)" }} data-testid="profile-avatar" />
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <h2 style={{ fontSize: 24 }} data-testid="profile-name">{user.name}</h2>
              {user.seller_verified && <CheckCircle2 size={18} className="badge-verified" />}
            </div>
            <div className="ss-muted" style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}><Mail size={14} /> {user.email}</div>
            {user.username && <div className="ss-muted" style={{ fontSize: 14, marginTop: 2 }} data-testid="profile-username">@{user.username}</div>}
            <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
              {user.founding_seller && <span className="badge-founding"><Ribbon size={12} /> Founding Member #{user.founding_number}</span>}
              {user.is_admin && <span className="ss-pill pill--ended">Admin</span>}
              {user.store_name && <span className="ss-pill pill--delivered"><Store size={12} /> {user.store_name}</span>}
            </div>
          </div>
        </div>

        <div className="ss-grid grid-3" style={{ marginTop: 24 }}>
          <div className="ss-card stat-card"><div><div className="stat-val">{user.following?.length || 0}</div><div className="stat-label">Following</div></div></div>
          <div className="ss-card stat-card"><div><div className="stat-val">{user.invite_quota || 10}</div><div className="stat-label">Invites</div></div></div>
          <div className="ss-card stat-card"><div><div className="stat-val">{user.email_verified ? "Yes" : "No"}</div><div className="stat-label">Email verified</div></div></div>
        </div>

        <div style={{ display: "flex", gap: 12, marginTop: 24, flexWrap: "wrap" }}>
          <button className="ss-btn ss-btn-ghost" onClick={() => navigate("/support")} data-testid="profile-support">Contact support</button>
          <button className="ss-btn ss-btn-ghost" style={{ color: "var(--red)", borderColor: "var(--red)" }} onClick={async () => { await logout(); navigate("/landing"); }} data-testid="profile-logout"><LogOut size={16} /> Sign out</button>
        </div>
      </div>

      <AccountSettings />

      <BlockedList />

      {!user.is_admin && (
        <div className="ss-card" style={{ padding: 22, marginTop: 18, border: "1px solid var(--red)" }} data-testid="danger-zone">
          <h3 style={{ fontSize: 16, fontWeight: 800, color: "var(--red)", display: "flex", alignItems: "center", gap: 8 }}><AlertTriangle size={17} /> Danger zone</h3>
          <p className="ss-muted" style={{ fontSize: 14, margin: "8px 0 14px", lineHeight: 1.6 }}>
            Permanently delete your account and personal data. This cannot be undone.
          </p>
          <button className="ss-btn ss-btn-ghost" style={{ color: "var(--red)", borderColor: "var(--red)" }} onClick={() => setShowDelete(true)} data-testid="delete-account-button">
            <Trash2 size={16} /> Delete my account
          </button>
        </div>
      )}

      {showDelete && <DeleteAccountModal onClose={() => setShowDelete(false)} />}
    </div>
  );
}
